import { useWorkspaceNavigate } from '../../workspace-path';
import { useLiveInvalidation } from '../../live/use-live';
import { ENTITY_HISTORY_TOPICS, type EntityHistoryType } from '../../live/topics';
import { HistoryList } from './history-list';
import { useHistory, useHistoryMembers } from './use-history';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ux-pass-2 · one record's History — `GET /history/:type/:id`.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The owner: *"This is needed in every single entity of the app."* A task, a project, a contract, a
 * line item, an invoice, an external cost, a person, a leave and an overtime request all mount
 * THIS, so all nine read the same endpoint into the same table — and the hosts stopped carrying a
 * history in their own data (the task route, the person hook and the contracts route each fetched
 * one of three shapes, unpaged).
 *
 * ⚠️ **It reads for itself, when it is mounted.** A task's History tab costs nothing until it is
 * opened, where the old one was a tenth request on every task open whether or not anybody looked.
 *
 * ⚠️ `showEntity` is the project's: U-9 makes its history span its members, costs, invoices and
 * contracts, and a row there has to say which of them it is about.
 */
export function EntityHistory({
  type,
  id,
  showEntity = false,
}: {
  readonly type: EntityHistoryType;
  readonly id: string;
  readonly showEntity?: boolean;
}) {
  const go = useWorkspaceNavigate();
  const history = useHistory(`/history/${type}/${encodeURIComponent(id)}`);
  const members = useHistoryMembers();

  useLiveInvalidation(ENTITY_HISTORY_TOPICS[type], history.reload);

  return (
    <HistoryList
      entries={history.entries}
      loading={history.loading}
      failure={history.failure}
      onRetry={() => void history.reload()}
      query={history.query}
      members={members}
      showEntity={showEntity}
      columnsKey={`history-${type}`}
      onOpenPath={(path) => {
        go(path);
      }}
    />
  );
}
